
import { useEffect } from 'react';

export default function HomePage() {
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (navbar) {
        if (window.scrollY > 50) {
          navbar.classList.add('bg-white', 'shadow-md');
          navbar.classList.remove('bg-transparent');
        } else {
          navbar.classList.remove('bg-white', 'shadow-md');
          navbar.classList.add('bg-transparent');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav id="navbar" className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/962da41f-d47e-4ed2-8e07-ae5776763e1f.png" 
                alt="VROHORITI SH.P.K. Logo" 
                className="h-12 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">VROHORITI SH.P.K.</span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">About</button>
              <button onClick={() => scrollToSection('products')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5EFE6] via-[#FFD4D4] to-[#B8A4D4]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start gap-12 mb-20">
            <div className="lg:w-3/5 pt-12">
              <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full mb-8 shadow-sm">
                <div className="w-2 h-2 bg-gradient-to-r from-[#B8A4D4] to-[#FFD4D4] rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">VROHORITI SH.P.K.</span>
              </div>
              
              <h1 className="text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight mb-8">
                Modern iOS Applications for Enhanced Security
              </h1>
            </div>
            
            <div className="lg:w-2/5 pt-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                A team of experts developing modern applications for the iOS platform. Our products enhance user experience and improve mobile device security.
              </p>
            </div>
          </div>
          
          <div className="mt-20">
            <div className="relative w-full h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/034330edea9e82d581571ecb03040712.jpg"
                alt="Development Team"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between mb-16">
            <div className="inline-flex items-center px-6 py-2 border border-white/30 rounded-full">
              <span className="text-sm font-medium text-white">Our Products</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-2xl">
              VPN & Cleaner Applications
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group hover:scale-105 transition-transform duration-300">
              <div className="aspect-square w-full bg-gradient-to-br from-[#B8A4D4] to-[#FFD4D4] flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20VPN%20application%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20shield%20icon%20and%20encrypted%20network%20visualization%2C%20clean%20iOS%20design%2C%20purple%20and%20pink%20gradient%20colors%2C%20professional%20app%20screenshot%2C%20minimalist%20UI%20design%2C%20security%20focused%20mobile%20application&width=600&height=600&seq=vpn-app-001&orientation=squarish"
                  alt="VPN Application"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-900 p-6">
                <h3 className="text-xl font-bold text-white mb-2">VPN Application</h3>
                <p className="text-sm text-gray-400">Secure your connection with military-grade encryption and global server access</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group hover:scale-105 transition-transform duration-300">
              <div className="aspect-square w-full bg-gradient-to-br from-[#FFD4D4] to-[#F5EFE6] flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20Cleaner%20application%20interface%20showing%20storage%20optimization%20and%20junk%20file%20removal%20on%20iPhone%20screen%2C%20clean%20modern%20design%2C%20beige%20and%20pink%20gradient%20colors%2C%20professional%20mobile%20app%20screenshot%2C%20minimalist%20interface%2C%20system%20cleaning%20utility%20application&width=600&height=600&seq=cleaner-app-001&orientation=squarish"
                  alt="Cleaner Application"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-900 p-6">
                <h3 className="text-xl font-bold text-white mb-2">Cleaner Application</h3>
                <p className="text-sm text-gray-400">Optimize device performance by removing junk files and freeing up storage space</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group hover:scale-105 transition-transform duration-300">
              <div className="aspect-square w-full bg-gradient-to-br from-[#F5EFE6] to-[#B8A4D4] flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20privacy%20protection%20features%20interface%20showing%20data%20encryption%20and%20secure%20browsing%20on%20iPhone%2C%20modern%20app%20design%2C%20purple%20and%20beige%20gradient%20colors%2C%20professional%20security%20application%20screenshot%2C%20clean%20minimalist%20UI%2C%20privacy%20focused%20mobile%20app&width=600&height=600&seq=privacy-app-001&orientation=squarish"
                  alt="Privacy Protection"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-900 p-6">
                <h3 className="text-xl font-bold text-white mb-2">Privacy Protection</h3>
                <p className="text-sm text-gray-400">Advanced privacy features to keep your personal data safe and secure</p>
              </div>
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl overflow-hidden group hover:scale-105 transition-transform duration-300">
              <div className="aspect-square w-full bg-gradient-to-br from-[#B8A4D4] to-[#F5EFE6] flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20performance%20optimization%20interface%20showing%20speed%20boost%20and%20battery%20management%20on%20iPhone%20screen%2C%20modern%20application%20design%2C%20gradient%20purple%20and%20beige%20colors%2C%20professional%20app%20screenshot%2C%20clean%20minimalist%20interface%2C%20system%20optimization%20utility&width=600&height=600&seq=performance-app-001&orientation=squarish"
                  alt="Performance Boost"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-900 p-6">
                <h3 className="text-xl font-bold text-white mb-2">Performance Boost</h3>
                <p className="text-sm text-gray-400">Enhance device speed and battery life with intelligent optimization</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-3/5">
              <div className="inline-flex items-center px-5 py-2 bg-gray-200 rounded-lg mb-6">
                <span className="text-sm font-medium text-gray-700">About VROHORITI SH.P.K.</span>
              </div>
              
              <h2 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-12">
                Expert Team Building Secure Solutions
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                We are dedicated to creating innovative iOS applications that prioritize user security and experience. Our team combines technical expertise with a deep understanding of mobile security challenges to deliver products that users can trust.
              </p>
            </div>
            
            <div className="lg:w-2/5">
              <div className="bg-[#2C4A3E] rounded-3xl p-8 relative">
                <div className="flex items-start justify-between mb-6">
                  <span className="text-sm text-white/70">Our Mission</span>
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-white/50 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/50 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-white/50 rounded-full"></div>
                  </div>
                </div>
                
                <p className="text-white text-lg leading-relaxed mb-8">
                  "Our commitment is to deliver high-quality, secure applications that enhance the iOS experience. We believe in continuous innovation and putting user security at the forefront of everything we create."
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">V</span>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">VROHORITI Team</div>
                      <div className="text-white/70 text-xs">Development Experts</div>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
                    <i className="ri-arrow-right-line text-white text-lg"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start justify-between mb-16 gap-8">
            <h2 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight lg:w-1/2">
              Advanced Features for Modern Users
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed lg:w-2/5">
              Our applications are built with cutting-edge technology to provide the best security and performance for your iOS devices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-3xl p-8 lg:col-span-1">
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20digital%20security%20shield%20with%20encryption%20symbols%20and%20lock%20icons%2C%20modern%20minimalist%20design%2C%20purple%20and%20pink%20gradient%20colors%2C%20clean%20geometric%20shapes%2C%20security%20concept%20visualization%2C%20professional%20tech%20illustration&width=500&height=400&seq=security-feature-001&orientation=landscape"
                  alt="Enhanced Security"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Enhanced Security</h3>
              <p className="text-gray-600 leading-relaxed">
                Military-grade encryption protocols protect your data and online activities from unauthorized access and cyber threats.
              </p>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Easy Interface</h3>
              <div className="mt-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Clean%20and%20intuitive%20iOS%20application%20user%20interface%20design%20showing%20simple%20navigation%20and%20modern%20controls%2C%20minimalist%20mobile%20app%20screen%2C%20beige%20and%20purple%20gradient%20colors%2C%20user-friendly%20interface%20visualization%2C%20professional%20UI%20design%20illustration&width=500&height=400&seq=interface-feature-001&orientation=landscape"
                  alt="Easy Interface"
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Fast Performance</h3>
              <div className="mt-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Speed%20and%20performance%20visualization%20with%20dynamic%20motion%20lines%20and%20fast%20processing%20symbols%2C%20modern%20tech%20illustration%2C%20gradient%20pink%20and%20beige%20colors%2C%20performance%20optimization%20concept%2C%20clean%20minimalist%20design%2C%20professional%20technology%20graphic&width=500&height=400&seq=performance-feature-001&orientation=landscape"
                  alt="Fast Performance"
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 bg-[#F8F8F8] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 opacity-10">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <path d="M 20 100 Q 50 20, 100 50 T 180 100" stroke="currentColor" fill="none" strokeWidth="2" className="text-gray-900"/>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-0 items-stretch">
            <div className="lg:w-1/3 relative z-10 lg:-mr-12">
              <div className="w-full h-96 lg:h-full rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20satisfied%20business%20person%20using%20smartphone%20with%20confident%20expression%2C%20neutral%20gray%20background%2C%20modern%20professional%20photography%2C%20high%20quality%20headshot%2C%20corporate%20style%20portrait%2C%20natural%20lighting&width=500&height=700&seq=testimonial-user-001&orientation=portrait"
                  alt="User Testimonial"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="lg:w-2/3 bg-[#0D0D0D] rounded-3xl p-12 lg:p-16 relative z-0 mt-8 lg:mt-0">
              <div className="text-[#F5EFE6] text-6xl mb-8">"</div>
              <p className="text-white text-2xl lg:text-3xl leading-relaxed mb-12">
                The VPN application has completely transformed how I browse online. The security features are top-notch, and the interface is incredibly intuitive. I feel confident knowing my data is protected.
              </p>
              <div className="mb-8">
                <div className="text-white font-semibold text-lg">Michael Anderson</div>
                <div className="text-white/70 text-sm">Technology Consultant</div>
              </div>
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 bg-[#FFB8B8] px-8 py-3 rounded-full cursor-pointer hover:bg-[#FFD4D4] transition-colors whitespace-nowrap">
                  <span className="text-gray-900 font-medium text-sm">View More Reviews</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6">
            Ready to Enhance Your Mobile Security?
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Join thousands of users protecting their iOS devices
          </p>
          
          <div className="mt-16">
            <div className="flex justify-center gap-8 flex-wrap">
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20VPN%20application%20interface%20with%20secure%20connection%20active%2C%20modern%20iOS%20app%20screenshot%20on%20device%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20mobile%20app%20presentation%2C%20security%20application%20display&width=400&height=600&seq=cta-mockup-001&orientation=portrait"
                alt="VPN App"
                className="w-64 h-auto rounded-3xl shadow-2xl"
              />
              <img 
                src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20Cleaner%20application%20interface%20with%20storage%20optimization%20screen%2C%20modern%20iOS%20app%20on%20device%2C%20clean%20white%20background%2C%20professional%20product%20photography%2C%20mobile%20utility%20app%20presentation%2C%20system%20cleaning%20display&width=400&height=600&seq=cta-mockup-002&orientation=portrait"
                alt="Cleaner App"
                className="w-64 h-auto rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#2C4A3E]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-extrabold text-white mb-6">Get in Touch</h2>
            <p className="text-xl text-white/80">We're here to help with any questions about our applications</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/15 transition-colors">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-3">Address</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Lagjja Periferike, Rruga Korce-Bilisht, Km. i II, zk. 1101
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/15 transition-colors">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-3">Location</h3>
              <p className="text-white/80 text-sm">City: <strong className="text-white">KORCE</strong></p>
              <p className="text-white/80 text-sm">Zip: <strong className="text-white">7000</strong></p>
              <p className="text-white/80 text-sm">Country: <strong className="text-white">Albania</strong></p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/15 transition-colors md:col-span-2 lg:col-span-1">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-3">Contact</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-mail-line text-white/80"></i>
                  <a href="mailto:support@vrohoritishpk.com" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
                    support@vrohoritishpk.com
                  </a>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <i className="ri-phone-line text-white/80"></i>
                  <a href="tel:+16419343300" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
                    +1 6419343300
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-20">
            <div>
              <h4 className="text-white font-bold text-sm mb-4">Company</h4>
              <div className="w-12 h-px bg-gray-700 mb-4"></div>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Team</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Careers</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Press</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm mb-4">Products</h4>
              <div className="w-12 h-px bg-gray-700 mb-4"></div>
              <ul className="space-y-3">
                <li><a href="#products" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">VPN App</a></li>
                <li><a href="#products" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Cleaner App</a></li>
                <li><a href="#features" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Features</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Updates</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm mb-4">Support</h4>
              <div className="w-12 h-px bg-gray-700 mb-4"></div>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Documentation</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">FAQ</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold text-sm mb-4">Legal</h4>
              <div className="w-12 h-px bg-gray-700 mb-4"></div>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Licenses</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img 
                  src="https://public.readdy.ai/ai/img_res/962da41f-d47e-4ed2-8e07-ae5776763e1f.png" 
                  alt="VROHORITI SH.P.K. Logo" 
                  className="h-10 w-auto"
                />
                <div>
                  <div className="text-white font-extrabold text-2xl">VROHORITI</div>
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-px bg-gray-700"></div>
                    <span className="text-gray-400 text-xs">SH.P.K.</span>
                  </div>
                </div>
              </div>

              <div className="text-center md:text-right">
                <p className="text-gray-400 text-sm mb-2">© 2025 VROHORITI SH.P.K. All rights reserved</p>
                <div className="flex items-center justify-center md:justify-end gap-4">
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-twitter-x-fill text-xl"></i>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 text-xs hover:text-gray-400 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
